import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Plus, Edit3, Eye, RefreshCw, Ban, CheckCircle2 } from 'lucide-react';
import { useNotification } from '@kardume/hooks';
import { adminApi, extractErrorMessage, formatNumber } from '@kardume/services';
import type {
  MerchantEmployeeDto,
  MerchantEmployeeRequestDto,
  MerchantAccessProfileDto,
  MerchantRole,
  AccountStatus,
  Tone,
  ComboItem,
} from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Table,
  TableContainer,
  Badge,
  Button,
  ActionButton,
  Avatar,
  Switch,
  SearchInput,
  Modal,
  TextInput,
  PhoneInput,
  DocumentInput,
  SearchableComboBox,
  formatCpf,
  formatPhone,
  Select,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';
import EmployeeDetailDrawer from '../components/EmployeeDetailDrawer';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;
  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const Person = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  strong {
    display: block;
    color: ${(p) => p.theme.textPrimary};
  }
  .email {
    font-size: 0.82rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.4rem;
  justify-content: flex-end;
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const ToggleField = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: ${(p) => p.theme.textMuted};
`;

const ROLE_OPTIONS = [
  { value: 'STAFF', label: 'Funcionário' },
  { value: 'OWNER', label: 'Proprietário' },
];

const ROLE_ITEMS: ComboItem[] = ROLE_OPTIONS.map((o) => ({ id: o.value, label: o.label }));

const ROLE_FILTER = [{ value: '', label: 'Todos os cargos' }, ...ROLE_OPTIONS];

const ROLE_LABELS: Record<MerchantRole, string> = { OWNER: 'Proprietário', STAFF: 'Funcionário' };

const STATUS_LABELS: Record<AccountStatus, string> = {
  ACTIVE: 'Ativo',
  PENDING_ACTIVATION: 'Pendente',
  INACTIVE: 'Inativo',
  BLOCKED: 'Bloqueado',
};

const STATUS_FILTER = [
  { value: '', label: 'Todos os status' },
  { value: 'ACTIVE', label: 'Ativo' },
  { value: 'INACTIVE', label: 'Inativo' },
  { value: 'BLOCKED', label: 'Bloqueado' },
];

function statusTone(status: AccountStatus): Tone {
  if (status === 'ACTIVE') return 'success';
  if (status === 'PENDING_ACTIVATION') return 'warning';
  if (status === 'BLOCKED') return 'danger';
  return 'secondary';
}

const roleTone = (role: MerchantRole): Tone => (role === 'OWNER' ? 'danger' : 'info');
const isAccessAllowed = (e: MerchantEmployeeDto): boolean => e.accessEnabled ?? e.status === 'ACTIVE';

interface EmployeeForm {
  firstName: string;
  lastName: string;
  email: string;
  document: string;
  phone: string;
  role: MerchantRole;
  accessEnabled: boolean;
  accessProfileId: string;
}

const EMPTY_FORM: EmployeeForm = {
  firstName: '',
  lastName: '',
  email: '',
  document: '',
  phone: '',
  role: 'STAFF',
  accessEnabled: true,
  accessProfileId: '',
};

const EmployeesPage: React.FC = () => {
  const { showNotification } = useNotification();
  const { data, loading, error, reload } = useAsync(() => adminApi.listEmployees({ size: 100 }), []);
  const profilesState = useAsync(() => adminApi.listAccessProfiles(), []);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EmployeeForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState<MerchantEmployeeDto | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const employees = useMemo<MerchantEmployeeDto[]>(() => data?.content ?? [], [data]);
  const profiles = useMemo<MerchantAccessProfileDto[]>(() => profilesState.data ?? [], [profilesState.data]);

  const profileItems = useMemo<ComboItem[]>(
    () => [{ id: '', label: 'Sem perfil' }, ...profiles.map((p) => ({ id: p.id, label: p.name }))],
    [profiles]
  );

  const stats = useMemo(
    () => ({
      total: data?.totalElements ?? employees.length,
      active: employees.filter((e) => e.status === 'ACTIVE').length,
      access: employees.filter(isAccessAllowed).length,
      owners: employees.filter((e) => e.role === 'OWNER').length,
    }),
    [data, employees]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return employees.filter((e) => {
      const matchesRole = !roleFilter || e.role === roleFilter;
      const matchesStatus = !statusFilter || e.status === statusFilter;
      const matchesTerm =
        !term ||
        [`${e.firstName} ${e.lastName}`, e.email, e.document ?? ''].some((v) => v.toLowerCase().includes(term));
      return matchesRole && matchesStatus && matchesTerm;
    });
  }, [employees, search, roleFilter, statusFilter]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setIsModalOpen(true);
  };

  const openEdit = (employee: MerchantEmployeeDto) => {
    setEditingId(employee.id);
    setForm({
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: employee.email,
      document: employee.document ?? '',
      phone: employee.phone ?? '',
      role: employee.role,
      accessEnabled: isAccessAllowed(employee),
      accessProfileId: employee.accessProfileId ?? '',
    });
    setIsModalOpen(true);
  };

  const openDetail = (employee: MerchantEmployeeDto) => {
    setDetail(employee);
    setDetailOpen(true);
  };

  const setField = <K extends keyof EmployeeForm>(key: K, value: EmployeeForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleAccess = async (employee: MerchantEmployeeDto) => {
    const allowed = isAccessAllowed(employee);
    try {
      await adminApi.setEmployeeStatus(employee.id, {
        accessEnabled: !allowed,
        status: allowed ? 'BLOCKED' : 'ACTIVE',
      });
      showNotification(allowed ? 'Acesso bloqueado.' : 'Acesso liberado.', 'success');
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível alterar o acesso.'), 'error');
    }
  };

  const handleSubmit = async () => {
    if (saving) return;
    const missing: string[] = [];
    if (!form.firstName.trim()) missing.push('Nome');
    if (!form.lastName.trim()) missing.push('Sobrenome');
    if (!form.email.trim()) missing.push('E-mail');
    if (!form.document.trim()) missing.push('CPF');
    if (!form.phone.trim()) missing.push('Telefone');
    if (!form.role) missing.push('Cargo');
    if (form.role !== 'OWNER' && !form.accessProfileId) missing.push('Perfil de acesso');
    if (missing.length) {
      showNotification('Preencha os campos obrigatorios: ' + missing.join(', ') + '.', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload: MerchantEmployeeRequestDto = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        document: form.document,
        phone: form.phone,
        role: form.role,
        accessEnabled: form.accessEnabled,
        accessProfileId: form.accessProfileId ? form.accessProfileId : null,
      };
      if (editingId) {
        await adminApi.updateEmployee(editingId, payload);
        showNotification('Funcionário atualizado com sucesso.', 'success');
      } else {
        await adminApi.createEmployee(payload);
        showNotification('Funcionário criado. A senha temporária foi enviada por e-mail.', 'success');
      }
      setIsModalOpen(false);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível salvar o funcionário.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Funcionários"
        subtitle="Gestão da equipe da loja e seus perfis de acesso."
        actions={
          <Button variant="primary" onClick={openCreate}>
            <Plus size={16} /> Novo funcionário
          </Button>
        }
      />

      <StatsGrid>
        <StatCard label="Total da equipe" value={formatNumber(stats.total)} />
        <StatCard label="Ativos" value={formatNumber(stats.active)} />
        <StatCard label="Acesso liberado" value={formatNumber(stats.access)} />
        <StatCard label="Proprietários" value={formatNumber(stats.owners)} />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar por nome, e-mail ou CPF..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select options={ROLE_FILTER} value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} />
        <Select options={STATUS_FILTER} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} />
        <ActionButton title="Atualizar" onClick={reload}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        onRetry={reload}
        isEmpty={filtered.length === 0}
        emptyTitle="Nenhum funcionário encontrado"
        emptyDescription="Cadastre membros da equipe para dar acesso ao painel da loja."
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Funcionário</th>
                <th style={{ textAlign: 'right' }}>Cargo</th>
                <th style={{ textAlign: 'right' }}>Status</th>
                <th style={{ textAlign: 'right' }}>Acesso</th>
                <th style={{ textAlign: 'right' }}>Perfil de acesso</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => {
                const allowed = isAccessAllowed(e);
                return (
                  <tr key={e.id}>
                    <td>
                      <Person>
                        <Avatar name={`${e.firstName} ${e.lastName}`} />
                        <div>
                          <strong>
                            {e.firstName} {e.lastName}
                          </strong>
                          <div className="email">{e.email}</div>
                        </div>
                      </Person>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Badge tone={roleTone(e.role)}>{ROLE_LABELS[e.role]}</Badge>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Badge tone={statusTone(e.status)}>{STATUS_LABELS[e.status]}</Badge>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <Badge tone={allowed ? 'success' : 'danger'}>{allowed ? 'Liberado' : 'Bloqueado'}</Badge>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {e.role === 'OWNER' ? (
                        <Badge tone="danger">Acesso total</Badge>
                      ) : e.accessProfileName ? (
                        <Badge tone="info">{e.accessProfileName}</Badge>
                      ) : (
                        <Badge tone="secondary">Sem perfil</Badge>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <RowActions>
                        <ActionButton title="Ver detalhes" onClick={() => openDetail(e)}>
                          <Eye size={16} />
                        </ActionButton>
                        <ActionButton title="Editar" onClick={() => openEdit(e)}>
                          <Edit3 size={16} />
                        </ActionButton>
                        {e.role !== 'OWNER' && (
                          <ActionButton title={allowed ? 'Bloquear acesso' : 'Liberar acesso'} onClick={() => toggleAccess(e)}>
                            {allowed ? <Ban size={16} /> : <CheckCircle2 size={16} />}
                          </ActionButton>
                        )}
                      </RowActions>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      <EmployeeDetailDrawer
        employee={detail}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        onEdit={(e) => {
          setDetailOpen(false);
          openEdit(e);
        }}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingId ? 'Editar funcionário' : 'Novo funcionário'}
        width="640px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSubmit} isLoading={saving}>
              {editingId ? 'Salvar alterações' : 'Criar'}
            </Button>
          </>
        }
      >
        <Form>
          <Row>
            <TextInput label="Nome *" value={form.firstName} onChange={(e) => setField('firstName', e.target.value)} />
            <TextInput label="Sobrenome *" value={form.lastName} onChange={(e) => setField('lastName', e.target.value)} />
          </Row>
          <Row>
            <TextInput label="E-mail *" type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} />
            <PhoneInput label="Telefone *" value={form.phone} onChange={(v) => setField('phone', v)} />
          </Row>
          <Row>
            <DocumentInput label="CPF *" documentType="CPF" placeholder="000.000.000-00" value={form.document} onChange={(v) => setField('document', v)} />
            <SearchableComboBox
              label="Cargo *"
              placeholder="Selecione o cargo"
              items={ROLE_ITEMS}
              valueId={form.role}
              onChange={(id) => setField('role', id as MerchantRole)}
            />
          </Row>
          <SearchableComboBox
            label="Perfil de acesso *"
            placeholder="Selecione um perfil"
            items={profileItems}
            valueId={form.accessProfileId}
            onChange={(id) => setField('accessProfileId', id)}
          />
          {form.role === 'OWNER' ? (
            <Hint>Proprietários têm acesso total à loja - o perfil de acesso é opcional.</Hint>
          ) : (
            <Hint>As permissões do funcionário são definidas pelo perfil de acesso selecionado.</Hint>
          )}
          <ToggleField>
            <Switch checked={form.accessEnabled} onChange={(v) => setField('accessEnabled', v)} />
            Acesso ao sistema ativo
          </ToggleField>
          <Hint>Ao criar, uma senha temporária é enviada por e-mail e a troca é obrigatória no primeiro acesso.</Hint>
        </Form>
      </Modal>
    </>
  );
};

export default EmployeesPage;
